import { formatDuration, intervalToDuration } from "date-fns";
import { surveysClient } from "modules/Surveys/client/surveysClient";
import { Surveys } from "modules/Surveys/surveys";
import { useCallback, useEffect, useMemo, useState } from "react";
import { BlockTypes, getBlockFullType } from "../helpers/blocks";
import { mainSurveyClient } from "../client/mainSurveyClient";
import { BlockNestedKey, Blockable, getSurveyTree } from "../helpers/blockTree";
import { BlockValues, DefaultPreviewDisplayType, getBlockDefaults } from "../helpers/blockForm";
import { get, set } from "lodash";
import { useQueryClient } from "react-query";
import { cacheKeys } from "config";
import { ExistingBlocks } from "modules/ExistingBlocks/existingBlocks";
import { SurveyBlocks } from "../surveyBlocks";
import { firstNestedPosition, PositionInstructions } from "@coolset/cs-ui";

export const useMainSurvey = (mainSurveyId: string) => {
  const queryClient = useQueryClient();

  const [mainSurvey, setMainSurvey] = useState<Surveys.Survey | undefined>();
  const [mainSurveyTree, setMainSurveyTree] = useState<Blockable>({} as Blockable);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [draggedBlockPath, setDraggedBlockPath] = useState<string | null>(null);
  const [draggedBlockPosition, setDraggedBlockPosition] = useState<string | null>(null);
  const [newBlockTypeToBeAdded, setNewBlockTypeToBeAdded] = useState<BlockTypes | null>(null);
  const [blockToAdd, setBlockToAdd] = useState<BlockToAdd | null>(null);
  const [existingBlockToAdd, setExistingBlockToAdd] = useState<ExistingBlocks.ExistingBlock | null>(null);
  const [blockToEdit, setBlockToEdit] = useState<BlockToEdit>({
    parentSurveyId: mainSurveyId,
    type: BlockTypes.MainSurvey,
  });
  const [subBlockToEdit, setSubBlockToEdit] = useState<BlockToEdit | null>(null);
  const [selectedBlocks, setSelectedBlocks] = useState<Blockable[]>([]);
  const [isTableEditOpen, setIsTableEditOpen] = useState<boolean>(false);
  const [tableBlocks, setTableBlocks] = useState<SurveyBlocks.Block[] | null>(null);
  const [selectedTableBlocks, setSelectedTableBlocks] = useState<SurveyBlocks.Block[]>([]);
  const [forceSave, setForceSave] = useState<boolean>(false);

  const updatedDurationString = useMemo(() => {
    if (!mainSurvey) {
      return "N/A";
    }

    const duration = intervalToDuration({
      start: new Date(mainSurvey.updatedAt),
      end: new Date(),
    });
    if (Object.values(duration).some((value) => value !== 0)) {
      return `Saved ${formatDuration(duration)} ago`;
    }

    return "Saved just now";
  }, [mainSurvey]);

  const handleSurveySaved = useCallback(() => {
    setMainSurvey((old) => ({ ...old!, updatedAt: new Date().toISOString() }));
    setIsSaving(false);
  }, []);

  const updateMainSurveyName = useCallback((blockValues: BlockValues) => {
    setMainSurvey((old) => ({ ...old!, title: blockValues.title || "", updatedAt: new Date().toISOString() }));
    setMainSurveyTree((old) => ({ ...old, title: blockValues.title || "" }));
  }, []);

  const updateSubBlock = useCallback(
    (updatedBlock?: SurveyBlocks.Block) => {
      if (!updatedBlock) return;

      const temp = [...(tableBlocks ?? [])];
      const blockIndex = tableBlocks?.findIndex((b) => b.id === updatedBlock.id);
      if (blockIndex === undefined || blockIndex === -1) return;

      temp[blockIndex] = updatedBlock;
      setTableBlocks(temp);
    },
    [tableBlocks]
  );

  const updateBlock = useCallback(
    (blockValues: BlockValues, updatedBlock?: SurveyBlocks.Block) => {
      const temp = { ...mainSurveyTree };

      const block = get(temp, blockToEdit.blockPath!);
      if (!block || (updatedBlock && block.id !== updatedBlock.id)) {
        updateSubBlock(updatedBlock);

        return;
      }

      block.title = blockValues.title || block.title;
      block.options = blockValues.options || block.options;
      setMainSurveyTree(temp);
      handleSurveySaved();
    },
    [blockToEdit, handleSurveySaved, mainSurveyTree, updateSubBlock]
  );

  const handleSetBlockToEdit = useCallback((block: BlockToEdit) => {
    setForceSave(true);
    setTimeout(() => setBlockToEdit(block), 100);
  }, []);

  const handleSetSubBlockToEdit = useCallback((block: BlockToEdit | null) => {
    setForceSave(true);
    setTimeout(() => setSubBlockToEdit(block), 100);
  }, []);

  const resetBlockToEdit = useCallback(() => {
    setBlockToEdit({
      parentSurveyId: mainSurveyId,
      type: BlockTypes.MainSurvey,
    });
  }, [mainSurveyId]);

  const resetSubBlockToEdit = useCallback(
    (newTableBlocks?: SurveyBlocks.Block[], positionInstructions?: PositionInstructions) => {
      const firstNestedPositionBlockId = firstNestedPosition(positionInstructions)?.[0];

      const firstNested = (newTableBlocks ?? tableBlocks ?? []).find((b) => String(b.id) === firstNestedPositionBlockId);

      if (!firstNested) {
        setSubBlockToEdit(null);
        setSelectedTableBlocks([]);
        return;
      }

      setSubBlockToEdit({
        id: firstNested.id,
        parentSurveyId: firstNested.surveyId,
        type: firstNested.contentType,
      });
      setSelectedTableBlocks([firstNested]);
    },
    [tableBlocks]
  );

  const refetchCurrentBlock = useCallback(() => {
    queryClient.invalidateQueries(cacheKeys.mainSurvey.getSurveyBlock);
  }, [queryClient]);

  const handleGetSurvey = useCallback(async () => {
    try {
      setIsLoading(true);
      const surveyData = await surveysClient.getSurvey(mainSurveyId!);
      setMainSurvey(surveyData.data);
      setMainSurveyTree(getSurveyTree(surveyData.data));
      setIsLoading(false);
    } catch (e) {
      console.error(e);
      setIsLoading(false);
    }
  }, [mainSurveyId]);

  // Like handleGetSurvey but skips the isLoading toggle, so the editor stays visible.
  const silentRefetchSurvey = useCallback(async () => {
    try {
      const surveyData = await surveysClient.getSurvey(mainSurveyId!);
      setMainSurvey(surveyData.data);
      setMainSurveyTree(getSurveyTree(surveyData.data));
    } catch (e) {
      console.error(e);
    }
  }, [mainSurveyId]);

  const getAndSetNested = useCallback(async (block: Blockable) => {
    const {
      data: { results: nestedBlocks },
    } = await mainSurveyClient.getSurveyBlocks(block.objectId);

    block[BlockNestedKey] = nestedBlocks
      .sort((a, b) => a.order - b.order)
      .map((nestedBlock) => ({
        id: nestedBlock.id,
        objectId: nestedBlock.objectId,
        title: nestedBlock.contentObject.title!,
        blockType: getBlockFullType(nestedBlock.contentType, nestedBlock.contentObject.type),
        options: nestedBlock.contentObject.options,
        parentSurveyId: block.objectId,
      }));
  }, []);

  const handleOnExpand = useCallback(
    async (blockPath: string, isExpanded: boolean) => {
      const temp = { ...mainSurveyTree };
      set(temp, blockPath + ".isExpanded", isExpanded);

      const block = get(temp, blockPath) as Blockable;
      if (isExpanded) {
        set(temp, blockPath + ".isGettingNested", true);
        setMainSurveyTree({ ...temp });

        await getAndSetNested(block);
        set(temp, blockPath + ".isGettingNested", false);
      }
      setMainSurveyTree({ ...temp });
    },
    [mainSurveyTree, setMainSurveyTree, getAndSetNested]
  );

  const handleUpdateTreeAfterBlockChange = useCallback(
    async (oldTree: Blockable, block: Blockable, parent: Blockable, parentPath: string, toIndex: number) => {
      setBlockToEdit({
        id: block.id,
        type: block.blockType,
        parentSurveyId: parent.objectId,
        blockPath: parentPath + `["${BlockNestedKey}"][${toIndex}]`,
      });

      if (!parent.isExpanded) {
        await handleOnExpand(parentPath, true);
      } else {
        parent[BlockNestedKey]!.splice(toIndex, 0, block);
      }
      setMainSurveyTree(oldTree);
      setBlockToAdd(null);
      setExistingBlockToAdd(null);
      setSelectedBlocks([block]);
    },
    [handleOnExpand, setBlockToEdit, setMainSurveyTree]
  );

  const handleChangedOrAddedSubBlock = useCallback((block?: SurveyBlocks.Block) => {
    if (block) {
      setTableBlocks((prevBlocks) => {
        const blockIndex = prevBlocks?.findIndex((b) => b.id === block.id);
        if (blockIndex === undefined || blockIndex === -1) {
          return [...(prevBlocks || []), block];
        }

        return (prevBlocks || []).map((b) => (b.id === block.id ? block : b));
      });
      setSubBlockToEdit({
        id: block.id,
        parentSurveyId: block.surveyId,
        type: block.contentType,
      });
      setSelectedTableBlocks([block]);
    }

    setBlockToAdd(null);
    setExistingBlockToAdd(null);
    setDraggedBlockPosition(null);
    setDraggedBlockPath(null);
  }, []);

  const handleAddBlock = useCallback(
    async (fieldId: string, title: string) => {
      if (!blockToAdd) {
        return;
      }

      const { callBack, toIndex, type, subType, parentSurveyId, parentPath } = blockToAdd;

      const isHadToTable = !!blockToAdd.position;

      const newBlock = {
        order: toIndex ?? 0,
        isRequired: false,
        previewDisplayType: DefaultPreviewDisplayType,
        contentType: type,
        contentObject: {
          ...getBlockDefaults(type, subType),
          title,
          ...(fieldId ? { fieldId } : null),
          type: subType,
        },
      };

      try {
        const { data: blockRes } = await mainSurveyClient.createSurveyBlock(parentSurveyId, newBlock);

        if (!isHadToTable) {
          const temp = { ...mainSurveyTree };
          const parent = (get(temp, parentPath ?? "") || temp) as Blockable;
          const block = {
            id: blockRes.id,
            objectId: blockRes.objectId,
            title: newBlock.contentObject.title!,
            blockType: getBlockFullType(newBlock.contentType, newBlock.contentObject.type),
            parentSurveyId: parentSurveyId,
          };

          handleUpdateTreeAfterBlockChange(temp, block, parent, parentPath ?? "", toIndex ?? 0);
        } else {
          handleChangedOrAddedSubBlock(blockRes);
        }

        callBack?.(blockRes);
      } catch (e: any) {
        return Array.isArray(e?.response?.data?.field_id) ? e.response.data.field_id[0] : "unknown error";
      }
    },

    [blockToAdd, handleChangedOrAddedSubBlock, handleUpdateTreeAfterBlockChange, mainSurveyTree]
  );

  const handleAddExistingBlock = useCallback(
    async (options: {
      parentId: number | string;
      parent?: Blockable;
      toIndex?: number;
      parentPath?: string;
      position?: string;
      callBack?: (block: SurveyBlocks.Block) => void;
    }) => {
      const { parent, toIndex = 0, parentPath, position, parentId, callBack } = options;

      if (!existingBlockToAdd) {
        return;
      }

      const existingBlock = {
        order: toIndex,
        isRequired: false,
        previewDisplayType: DefaultPreviewDisplayType,
        contentType: existingBlockToAdd.contentType,
        objectId: existingBlockToAdd.id,
      };

      try {
        const { data: blockRes } = await mainSurveyClient.createSurveyBlock(parentId, existingBlock);
        if (parentPath !== undefined && parent) {
          const temp = { ...mainSurveyTree };
          const block = {
            id: blockRes.id,
            objectId: blockRes.objectId,
            title: blockRes.contentObject.title!,
            blockType: blockRes.contentType,
            parentSurveyId: parentId,
          };

          handleUpdateTreeAfterBlockChange(temp, block, parent, parentPath, toIndex);
        }

        if (position) {
          handleChangedOrAddedSubBlock(blockRes);
        }

        callBack?.(blockRes);
      } catch (e: any) {
        return Array.isArray(e?.response?.data?.field_id) ? e.response.data.field_id[0] : "unknown error";
      }
    },

    [existingBlockToAdd, handleChangedOrAddedSubBlock, handleUpdateTreeAfterBlockChange, mainSurveyTree]
  );

  useEffect(() => {
    handleGetSurvey();
  }, [handleGetSurvey]);

  useEffect(() => {
    queryClient.invalidateQueries(cacheKeys.mainSurvey.getSurveyBlock);
  }, [blockToEdit, subBlockToEdit, queryClient]);

  useEffect(() => {
    setSubBlockToEdit(null);
    setTableBlocks(null);
  }, [blockToEdit]);

  return {
    mainSurvey,
    mainSurveyTree,
    setMainSurveyTree,
    isLoading,
    updatedDurationString,
    handleAddBlock,
    handleAddExistingBlock,
    handleUpdateTreeAfterBlockChange,
    handleOnExpand,
    setMainSurvey,
    draggedBlockPath,
    setDraggedBlockPath,
    draggedBlockPosition,
    setDraggedBlockPosition,
    newBlockTypeToBeAdded,
    setNewBlockTypeToBeAdded,
    blockToAdd,
    setBlockToAdd,
    blockToEdit,
    forceSave,
    setForceSave,
    subBlockToEdit,
    setSubBlockToEdit: handleSetSubBlockToEdit,
    existingBlockToAdd,
    setExistingBlockToAdd,
    selectedBlocks,
    setSelectedBlocks,
    setBlockToEdit: handleSetBlockToEdit,
    tableBlocks,
    setTableBlocks,
    selectedTableBlocks,
    setSelectedTableBlocks,
    getAndSetNested,
    resetBlockToEdit,
    resetSubBlockToEdit,
    isSaving,
    setIsSaving,
    isTableEditOpen,
    setIsTableEditOpen,
    handleChangedOrAddedSubBlock,
    handleSurveySaved,
    updateMainSurveyName,
    updateBlock,
    refetchSurvey: handleGetSurvey,
    silentRefetchSurvey,
    refetchCurrentBlock,
  };
};

export type BlockToEdit = {
  id?: string | number;
  parentSurveyId: string | number;
  type: BlockTypes;
  blockPath?: string;
};

export type BlockToAdd = {
  parentSurveyId: string | number;
  type: BlockTypes;
  parentPath?: string;
  subType?: SurveyBlocks.BlockSubType;
  toIndex?: number;
  position?: string;
  callBack?: (block: SurveyBlocks.Block) => void;
};

export type MainSurveyStore = ReturnType<typeof useMainSurvey>;
